import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import './apiKeys.scss';
import './companyMembers.scss';
import './companyRoles.scss';
import {
    getCompanyRoles,
    getModules,
    getAppPermissions,
    deleteCompanyRole,
} from '../../Redux/ActionCreators/settingAction';
import { useAppPermission } from '../../hooks/useAppPermission';
import { P } from '../../constants/rbac';

const CompanyRoles = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { can } = useAppPermission();
    const canCreate = can(P.SETTINGS_ROLES_CREATE);
    const canEdit = can(P.SETTINGS_ROLES_EDIT);
    const canDelete = can(P.SETTINGS_ROLES_DELETE);
    const [loading, setLoading] = useState(false);
    const [viewRole, setViewRole] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

    const companyRoles = useSelector((state) => state.setting.companyRoles || []);

    useEffect(() => {
        if (location.state?.flash) {
            setSnack({ open: true, message: location.state.flash, severity: 'success' });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([
                dispatch(getCompanyRoles()),
                dispatch(getModules()),
                dispatch(getAppPermissions()),
            ]);
            setLoading(false);
        };
        load();
    }, [dispatch]);

    const openCreate = () => {
        navigate('/company-roles/create');
    };

    const openEdit = (role) => {
        navigate(`/company-roles/${role.id}/edit`, { state: { role } });
    };

    const handleDelete = async (role) => {
        if (role.is_system) return;
        if (!window.confirm(`Delete custom role "${role.name}"?`)) return;
        const result = await dispatch(deleteCompanyRole(role.id));
        if (result?.type === 'DELETE_COMPANY_ROLE') {
            setSnack({ open: true, message: 'Role deleted.', severity: 'success' });
        } else {
            setSnack({
                open: true,
                message: result?.payload?.detail || 'Failed to delete role.',
                severity: 'error',
            });
        }
    };

    return (
        <div className="job-table-container roles-page">
            <div className="roles-page-header">
                <div className="roles-page-titles">
                    <h4>Roles & Permissions</h4>
                    <p>Manage roles and module access levels for your organization. Roles apply across all teams.</p>
                </div>
                <div className="roles-header-actions">
                    {canCreate && (
                        <button type="button" className="roles-btn-primary" onClick={openCreate}>
                            + Create Role
                        </button>
                    )}
                </div>
            </div>

            <div className="table-content" style={{ background: '#FFFFFF', borderRadius: '10px' }}>
                <div className="jv2-table-outer">
                    <div className="jv2-table-scroll">
                        <table className="jv2-table">
                            <thead>
                                <tr>
                                    <th>Role name</th>
                                    <th>Type</th>
                                    <th>Scope</th>
                                    <th>Permissions</th>
                                    <th style={{ textAlign: 'center', minWidth: 120 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="jv2-loading-cell">
                                            <div
                                                className="spinner-border"
                                                role="status"
                                                style={{ color: '#F75891', width: '28px', height: '28px' }}
                                            >
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : companyRoles.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="jv2-empty-cell">
                                            No roles found.
                                        </td>
                                    </tr>
                                ) : (
                                    companyRoles.map((role) => (
                                        <tr key={role.id}>
                                            <td>
                                                <div className="roles-role-cell">
                                                    <div className="roles-role-icon">
                                                        {(role.name || 'R').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="roles-role-name">{role.name}</div>
                                                        {role.description ? (
                                                            <div className="roles-role-desc">{role.description}</div>
                                                        ) : (
                                                            <div className="roles-role-desc">{role.code}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`roles-badge ${role.is_system ? 'system' : 'custom'}`}>
                                                    {role.is_system ? 'System Role' : 'Custom Role'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="roles-badge scope">
                                                    {role.data_scope === 'assigned' ? 'Assigned' : 'Team-wide'}
                                                </span>
                                            </td>
                                            <td>{role.permission_codes?.length ?? 0}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <div className="roles-actions-cell">
                                                    <button
                                                        type="button"
                                                        className="roles-action-icon"
                                                        title="View permissions"
                                                        onClick={() => setViewRole(role)}
                                                    >
                                                        <img src="/images/eye-black-icon.svg" alt="View" />
                                                    </button>
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            className="roles-action-icon"
                                                            title={role.is_system ? 'View details' : 'Edit role'}
                                                            onClick={() => openEdit(role)}
                                                        >
                                                            <img src="/images/edit-black-icon.svg" alt="Edit" />
                                                        </button>
                                                    )}
                                                    {!role.is_system && canDelete && (
                                                        <button
                                                            type="button"
                                                            className="roles-action-icon danger"
                                                            title="Delete role"
                                                            onClick={() => handleDelete(role)}
                                                        >
                                                            <img src="/images/delete-icon.svg" alt="Delete" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {viewRole && (
                <div className="upload-modal automation-modal">
                    <div className="modal-content member-modal-content" style={{ maxWidth: 640 }}>
                        <div className="modal-head">
                            <h3>{viewRole.name} permissions</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={() => setViewRole(null)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                        <div className="modal-input" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                                {viewRole.description || `${viewRole.code} · ${viewRole.data_scope}`}
                            </p>
                            {(viewRole.permission_codes || []).length === 0 ? (
                                <div>No permissions assigned.</div>
                            ) : (
                                <ul className="roles-view-list">
                                    {viewRole.permission_codes.map((code) => (
                                        <li key={code}>{code}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="d-flex justify-content-end" style={{ margin: '0 30px 20px 30px' }}>
                            <button className="upload-btn" onClick={() => setViewRole(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default CompanyRoles;
